import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Center,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { ApiKey } from 'api/auth/api.pb';
import {
  InformationAlert,
  PageHeaderWithIcon,
  renderStatusTag,
  renderTime,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { AddButton, DeleteActionButton } from 'components/DataEntry';
import { ExclamationTriangleIcon, KeyIcon } from 'components/Icons';
import { useTrackApiCall } from 'hooks/useTrackApiCall';

import { selectApiKeys } from '../selectors';
import { actions } from '../slice';

import { CreateApiKeyModal } from './CreateApiKeyModal';

const keyStatus = (key: ApiKey): string => {
  if (key.revocation_time) return 'Revoked';
  if (key.expiration_time && new Date(key.expiration_time) < new Date())
    return 'Expired';
  return 'Active';
};

export const ApiKeysTable: FC = () => {
  const dispatch = useDispatch();
  const createApiKeyDisclosure = useDisclosure();
  const { apiKeys, revokeApiKey } = useSelector(selectApiKeys);
  const { success: revoked } = useTrackApiCall(revokeApiKey);
  const [revokingId, setRevokingId] = useState<string>();

  useEffect(() => {
    dispatch(actions.getApiKeys());
  }, [dispatch]);

  useEffect(() => {
    if (revoked) {
      dispatch(actions.getApiKeys());
    }
  }, [revoked, dispatch]);

  const columns = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ row }) => (
        <Stack spacing={0}>
          <Text>{row?.name}</Text>
          {row?.description && (
            <Text fontSize="xs" color="gray.500" noOfLines={2}>
              {row.description}
            </Text>
          )}
        </Stack>
      ),
    },
    { header: 'Prefix', accessor: 'key_prefix', align: 'left' },
    {
      header: 'Created By',
      render: ({ row }) =>
        row?.created_by?.username || row?.created_by_user_id || '-',
      align: 'left',
    },
    {
      header: 'Status',
      render: ({ row }) => renderStatusTag(keyStatus(row)),
      align: 'left',
    },
    {
      header: 'Created',
      accessor: 'creation_time',
      render: renderTime,
      align: 'left',
    },
    {
      header: 'Expires',
      accessor: 'expiration_time',
      render: renderTime,
      align: 'left',
    },
    {
      header: 'Last Used',
      accessor: 'last_used_time',
      render: renderTime,
      align: 'left',
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <DeleteActionButton
            label="Revoke"
            header="Revoke API Key"
            type="API key"
            name={row?.name ?? ''}
            confirmationText={
              <Stack spacing={6}>
                <InformationAlert
                  icon={
                    <ExclamationTriangleIcon
                      color={customTheme.colors.red['400']}
                    />
                  }
                  description="Revoking this key is immediate and cannot be undone. Any script or integration using it will stop working right away."
                  color={customTheme.colors.red['400']}
                  bg={colorOpacity(customTheme.colors.red['400'], 0.1)}
                />
                <Text>
                  Please type in the API key name <b>{row?.name}</b> and revoke.
                </Text>
              </Stack>
            }
            isDisabled={keyStatus(row) === 'Revoked'}
            isLoading={revokeApiKey.loading && revokingId === row?.id}
            onConfirm={() => {
              setRevokingId(row?.id);
              dispatch(actions.revokeApiKey({ id: row?.id }));
            }}
          />
        </Center>
      ),
    },
  ];

  return (
    <Stack h="full" spacing={4} w="full">
      <HStack w="full" justify="space-between" align="center">
        <PageHeaderWithIcon label="API Keys" icon={<KeyIcon />} />
        <AddButton
          label="Create API Key"
          onClick={createApiKeyDisclosure.onOpen}
        />
      </HStack>

      <Box flex={1} minH={0} overflow="auto">
        <Table
          columns={columns}
          data={apiKeys.data}
          isLoading={apiKeys.loading}
        />
      </Box>
      {createApiKeyDisclosure.isOpen && (
        <CreateApiKeyModal
          isOpen={createApiKeyDisclosure.isOpen}
          onClose={createApiKeyDisclosure.onClose}
          onCreated={() => dispatch(actions.getApiKeys())}
        />
      )}
    </Stack>
  );
};
